import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Star } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "../../components/ui/sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { StatusBadge } from "../../components/admin/StatusBadge";
import { VerificationActions } from "../../components/admin/VerificationActions";
import { formatDate, formatDateTime } from "../../lib/dateUtils";
import { mediaUrl } from "../../lib/mediaUrl";

export default function PhotographerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [togglingFeatured, setTogglingFeatured] = useState(false);

  const load = () => {
    api.get(`/admin/photographers/${id}`).then(({ data }) => setData(data));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggleFeatured = async (featured) => {
    setTogglingFeatured(true);
    try {
      await api.put(`/admin/verifications/${id}/feature`, { featured });
      toast.success(featured ? "Featured on homepage" : "Removed from featured");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update featured status");
    } finally {
      setTogglingFeatured(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/admin/photographers/${id}/notes`, { note: note.trim() });
      setNote("");
      toast.success("Note added");
      load();
    } finally {
      setSavingNote(false);
    }
  };

  if (!data) return <p className="font-medium text-midnight-black/60" data-testid="photographer-detail-loading">Loading...</p>;
  const { profile, owner, sessions, participants, reviews, admin_notes, activity_logs } = data;
  const ratingAvg = reviews.length ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1) : null;

  return (
    <div data-testid="photographer-detail-page" className="max-w-5xl">
      <button onClick={() => navigate("/admin/photographers")} data-testid="back-to-photographers" className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-midnight-black/60 hover:text-stranger-orange mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Photographers
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <h2 className="font-extrabold uppercase text-2xl">{owner.first_name} {owner.last_name} — {profile.business_name}</h2>
          <p className="text-sm font-medium text-midnight-black/60">{owner.email} · {profile.location}</p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={profile.verification_status || "not_submitted"} testId="photographer-detail-verification" />
          <StatusBadge status={owner.status} testId="photographer-detail-account-status" />
          <div className="flex items-center gap-2 border-2 border-soft-gray rounded-full px-4 py-2">
            <span className="text-xs font-bold uppercase tracking-wide">Featured</span>
            <Switch data-testid="toggle-featured-switch" checked={!!profile.is_featured} disabled={togglingFeatured} onCheckedChange={toggleFeatured} />
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 mb-8" data-testid="photographer-detail-tabs">
          {["overview", "photos", "verification", "sessions", "participants", "reviews", "notes", "activity"].map((t) => (
            <TabsTrigger
              key={t}
              value={t}
              data-testid={`photographer-tab-${t}`}
              className="text-xs font-bold uppercase tracking-wide px-4 py-2 rounded-full border-2 border-soft-gray data-[state=active]:bg-stranger-orange data-[state=active]:text-clean-white data-[state=active]:border-stranger-orange"
            >
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" data-testid="photographer-tab-content-overview">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="border border-soft-gray rounded-xl p-4"><dt className="font-bold text-xs uppercase text-midnight-black/50 mb-1">Bio</dt><dd>{profile.bio || "Not set"}</dd></div>
            <div className="border border-soft-gray rounded-xl p-4"><dt className="font-bold text-xs uppercase text-midnight-black/50 mb-1">Years Experience</dt><dd>{profile.years_experience ?? "Not set"}</dd></div>
            <div className="border border-soft-gray rounded-xl p-4"><dt className="font-bold text-xs uppercase text-midnight-black/50 mb-1">Specialties</dt><dd>{profile.specialties?.length ? profile.specialties.join(", ") : "Not set"}</dd></div>
            <div className="border border-soft-gray rounded-xl p-4"><dt className="font-bold text-xs uppercase text-midnight-black/50 mb-1">Website / Social</dt><dd>{[profile.website, profile.instagram, profile.facebook, profile.tiktok].filter(Boolean).join(" · ") || "Not set"}</dd></div>
            <div className="border border-soft-gray rounded-xl p-4"><dt className="font-bold text-xs uppercase text-midnight-black/50 mb-1">Phone</dt><dd>{owner.phone || "Not set"}</dd></div>
            <div className="border border-soft-gray rounded-xl p-4"><dt className="font-bold text-xs uppercase text-midnight-black/50 mb-1">Joined</dt><dd>{formatDate(owner.created_at)}</dd></div>
            <div className="border border-soft-gray rounded-xl p-4"><dt className="font-bold text-xs uppercase text-midnight-black/50 mb-1">Average Rating</dt><dd className="flex items-center gap-1">{ratingAvg ? (<><Star className="w-3.5 h-3.5 text-stranger-orange fill-stranger-orange" /> {ratingAvg} ({reviews.length} reviews)</>) : "No reviews yet"}</dd></div>
            <div className="border border-soft-gray rounded-xl p-4"><dt className="font-bold text-xs uppercase text-midnight-black/50 mb-1">Payments</dt><dd className="text-midnight-black/50">Payments module not configured</dd></div>
          </dl>
        </TabsContent>

        <TabsContent value="photos" data-testid="photographer-tab-content-photos">
          <div className="mb-8">
            <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Profile Photo</h3>
            {profile.profile_photo_url ? (
              <img
                src={mediaUrl(profile.profile_photo_url)}
                alt={`${profile.business_name} profile`}
                className="w-40 h-40 rounded-2xl object-cover border border-soft-gray"
                data-testid="photographer-profile-photo"
              />
            ) : (
              <p className="text-sm text-midnight-black/50" data-testid="no-photographer-profile-photo">No profile photo uploaded.</p>
            )}
          </div>
          <div>
            <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">
              Portfolio ({profile.portfolio_urls?.length || 0})
            </h3>
            {profile.portfolio_urls?.length ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="photographer-portfolio-grid">
                {profile.portfolio_urls.map((url, i) => (
                  <img
                    key={url}
                    src={mediaUrl(url)}
                    alt={`Portfolio ${i + 1}`}
                    className="w-full aspect-square object-cover rounded-xl border border-soft-gray"
                    data-testid={`photographer-portfolio-photo-${i}`}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-midnight-black/50" data-testid="no-photographer-portfolio">No portfolio photos uploaded yet.</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="verification" data-testid="photographer-tab-content-verification">
          {profile.verification ? (
            <div className="border border-soft-gray rounded-2xl p-6 space-y-2 text-sm mb-6">
              <p><strong>Legal Name:</strong> {profile.verification.legal_name}</p>
              <p><strong>Business:</strong> {profile.verification.business_name}</p>
              <p><strong>Phone:</strong> {profile.verification.phone || "Not provided"}</p>
              <p><strong>Address:</strong> {profile.verification.address || "Not provided"}</p>
              <p><strong>Service Location:</strong> {profile.verification.service_location}</p>
              <p><strong>Website:</strong> {profile.verification.website || "Not provided"}</p>
              <p><strong>Instagram:</strong> {profile.verification.instagram || "Not provided"}</p>
              <p><strong>Portfolio Notes:</strong> {profile.verification.portfolio_note || "Not provided"}</p>
              <p><strong>Agreement Accepted:</strong> {profile.verification.agreement_accepted ? "Yes" : "No"}</p>
              <p><strong>Safety Standards Accepted:</strong> {profile.verification.safety_standards_accepted ? "Yes" : "No"}</p>
              <p><strong>Submitted:</strong> {formatDate(profile.verification.submitted_at)}</p>
              {profile.approved_by && <p><strong>Approved:</strong> {formatDate(profile.approved_date)}</p>}
              {profile.rejection_reason && <p className="text-red-600 font-bold">Rejection reason: {profile.rejection_reason}</p>}
              {profile.info_requested_message && <p className="text-stranger-orange font-bold">Info requested: {profile.info_requested_message}</p>}
              {profile.suspended_reason && <p className="text-red-600 font-bold">Suspended: {profile.suspended_reason}</p>}
            </div>
          ) : (
            <p className="text-sm text-midnight-black/60 mb-6" data-testid="no-verification-submitted">No verification application submitted yet.</p>
          )}
          <VerificationActions photographerId={profile.id} onDone={load} compact />
        </TabsContent>

        <TabsContent value="sessions" data-testid="photographer-tab-content-sessions">
          {sessions.length === 0 ? (
            <p className="text-sm text-midnight-black/60" data-testid="no-photographer-sessions">No sessions yet.</p>
          ) : (
            <div className="space-y-2" data-testid="photographer-sessions-list">
              {sessions.map((s) => (
                <div key={s.id} className="border border-soft-gray rounded-xl p-4 flex justify-between items-center text-sm font-medium">
                  <span>{s.title} · {formatDate(s.session_date)} · {s.registrations_count}/{s.capacity}</span>
                  <StatusBadge status={s.status} />
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="participants" data-testid="photographer-tab-content-participants">
          {participants.length === 0 ? (
            <p className="text-sm text-midnight-black/60" data-testid="no-photographer-participants">No participants yet.</p>
          ) : (
            <div className="space-y-2" data-testid="photographer-participants-list">
              {participants.map((p) => (
                <div key={p.booking_id} className="border border-soft-gray rounded-xl p-4 flex justify-between items-center text-sm font-medium">
                  <span>{p.stranger_name} · {p.session_title} ({formatDate(p.session_date)})</span>
                  <StatusBadge status={p.status} />
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="reviews" data-testid="photographer-tab-content-reviews">
          {reviews.length === 0 ? (
            <p className="text-sm text-midnight-black/60" data-testid="no-photographer-reviews">No reviews yet.</p>
          ) : (
            <div className="space-y-2" data-testid="photographer-reviews-list">
              {reviews.map((r) => (
                <div key={r.id} className="border border-soft-gray rounded-xl p-4 text-sm">
                  <div className="flex gap-0.5 mb-1">
                    {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 text-stranger-orange fill-stranger-orange" />)}
                  </div>
                  <p className="font-medium">&ldquo;{r.comment}&rdquo;</p>
                  <p className="text-xs font-bold uppercase text-midnight-black/50 mt-1">{r.stranger_name}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="notes" data-testid="photographer-tab-content-notes">
          <p className="text-xs text-midnight-black/50 mb-4">Internal notes are never visible to the photographer.</p>
          <div className="flex gap-3 mb-6">
            <Textarea data-testid="photographer-note-input" placeholder="Add an internal note..." value={note} onChange={(e) => setNote(e.target.value)} className="min-h-[80px]" />
            <Button data-testid="photographer-add-note-button" onClick={addNote} disabled={savingNote || !note.trim()} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full shrink-0 self-end">
              ADD NOTE
            </Button>
          </div>
          {admin_notes.length === 0 ? (
            <p className="text-sm text-midnight-black/60" data-testid="no-photographer-notes">No internal notes yet.</p>
          ) : (
            <div className="space-y-3" data-testid="photographer-notes-list">
              {[...admin_notes].reverse().map((n, i) => (
                <div key={i} className="border border-soft-gray rounded-xl p-4 text-sm">
                  <p className="font-medium">{n.note}</p>
                  <p className="text-xs font-bold uppercase text-midnight-black/40 mt-2">{n.admin_name} · {formatDateTime(n.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="activity" data-testid="photographer-tab-content-activity">
          {activity_logs.length === 0 ? (
            <p className="text-sm text-midnight-black/60" data-testid="no-photographer-activity">No recorded admin activity yet.</p>
          ) : (
            <div className="space-y-2" data-testid="photographer-activity-list">
              {activity_logs.map((log) => (
                <div key={log.id} className="border border-soft-gray rounded-xl p-4 text-sm flex justify-between">
                  <span>{log.admin_name} — {log.action.replace(/_/g, " ")}</span>
                  <span className="text-midnight-black/40 text-xs">{formatDateTime(log.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
